<template>
  <div v-if="attributionNodes.length > 0" class="control-attribution">
    <button
      type="button"
      class="control-attribution-button"
      aria-controls="attribution-list"
      :aria-label="
        shouldShowAttributions ? 'Hide attributions' : 'Show attributions'
      "
      :aria-expanded="shouldShowAttributions"
      @click="revealAttributions"
    >
      <ys-icon
        icon="ysInformationFill"
        size="small"
        class="control-attribution-icon"
      />
    </button>
    <section
      v-if="shouldShowAttributions"
      id="attribution-list"
      aria-label="Map attributions"
      class="control-attribution-link-list"
      data-test="control-attribution-link-list"
    >
      <template
        v-for="node in attributionNodes"
        :key="`${node.type}-${node.text}-${node.href}`"
      >
        <a
          v-if="node.type === 'link'"
          :href="node.href"
          target="_blank"
          rel="noopener noreferrer"
          class="attribution-link"
        >
          <ys-text type="link2">{{ node.text }}</ys-text>
        </a>
        <ys-text v-else type="body2">{{ node.text }}</ys-text>
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useMapManager } from '@/composables/map-x/use-map-manager'
import type { EventCallback } from '@onxmaps/map-x/events'
import { YsIcon, YsText } from '@onxmaps/yellowstone-ui'
import '@onxmaps/yellowstone-ui/icons/ysInformationFill'

type Attribution = {
  type: 'text' | 'link'
  text: string
  href?: string
}

/**
 * Minimal interface for a single Mapbox source cache entry.
 */
export interface MapboxSourceCache {
  used?: boolean
  usedForTerrain?: boolean
  getSource(): { attribution?: string }
}

/**
 * A source with an optional attribution string, as returned by the viewer map style object.
 */
export interface ActiveSource {
  id: string
  attribution?: string
}

// Structured attribution data for rendering
const attributionNodes = ref<Attribution[]>([])
const shouldShowAttributions = ref(false)

let cleanupHandler: (() => void) | null = null

const mapManager = useMapManager()

const revealAttributions = () => {
  shouldShowAttributions.value = !shouldShowAttributions.value
}

/**
 * Cache key to detect when active sources have changed
 * Format: "sourceId:attribution|sourceId:attribution|..."
 */
let _sourceCacheKey: string | null = null

/**
 * Returns only sources that are actively being used for rendering.
 * Filters by MapboxGL's source cache "used" and "usedForTerrain" flags.
 *
 * @returns Array of active sources with attributions, or undefined if sources haven't changed since last call (cache hit)
 */
function getActiveSources(): Array<ActiveSource> | undefined {
  const sources: Array<ActiveSource> = []

  if (!mapManager.viewer.map.style) {
    return sources
  }

  const sourceCaches =
    mapManager.viewer.map.style._mergedSourceCaches ||
    mapManager.viewer.map.style._sourceCaches ||
    {}

  // Build cache key from active source IDs + full attribution
  const cacheKeyParts: string[] = []

  for (const id in sourceCaches) {
    const sourceCache = sourceCaches[id]

    if (sourceCache.used || sourceCache.usedForTerrain) {
      const source = sourceCache.getSource()
      if (source.attribution) {
        sources.push({ id, attribution: source.attribution })
        // Cache key includes full attribution to detect any changes
        cacheKeyParts.push(`${id}:${source.attribution}`)
      }
    }
  }

  const cacheKey = cacheKeyParts.sort().join('|')

  // Return undefined if sources haven't changed
  // This signals to getAttributions() to skip reprocessing
  if (_sourceCacheKey === cacheKey) {
    return undefined
  }

  // Update cache key
  _sourceCacheKey = cacheKey

  return sources
}

/**
 * Returns structured attribution data from active map sources
 *
 * Parses attribution strings from TileJSON sources (plain text or HTML with links)
 * and returns framework-agnostic structured data for rendering.
 *
 * - Collects attributions from active sources (used/usedForTerrain flags)
 * - Deduplicates substring matches (shorter attributions removed if contained in longer ones)
 * - Parses strings using regex to extract text and links
 * - Returns structured Attribution[] ready for direct rendering
 *
 * Attribution strings can be:
 * - Plain text: "© OpenStreetMap contributors"
 * - HTML with links: "© <a href='https://openstreetmap.org'>OpenStreetMap</a> contributors"
 * - Multiple elements: "© <a href='https://osm.org'>OSM</a> | <a href='https://mapbox.com'>Mapbox</a>"
 *
 * @returns Array of Attribution objects with type ('text'|'link'), text, and optional href,
 *          or undefined if sources haven't changed
 */
function getAttributions(): Attribution[] | undefined {
  const sources = getActiveSources()

  // Early return if sources haven't changed
  // This skips all deduplication and parsing work
  if (sources === undefined) {
    return undefined
  }

  const attributionStrings: string[] = []

  /**
   * Collect unique attribution strings from a list of active sources.
   */
  for (const source of sources) {
    if (
      source.attribution &&
      !attributionStrings.includes(source.attribution)
    ) {
      attributionStrings.push(source.attribution)
    }
  }

  /**
   * Remove attributions that are substrings of longer attributions.
   *
   * Sorts by length ascending, then filters out any string that is contained
   * inside a longer string in the same list.
   */
  const filtered = attributionStrings.filter((attr) => String(attr).trim())
  filtered.sort((a, b) => a.length - b.length)

  // Deduplicate: remove attributions that are substrings of longer ones
  const deduplicated = filtered.filter((attrib, i) => {
    for (let j = i + 1; j < filtered.length; j++) {
      if (filtered[j].includes(attrib)) {
        return false
      }
    }
    return true
  })

  /**
   * Parse a single TileJSON attribution string into structured Attribution nodes.
   *
   * Handles:
   * - Plain text: "© OpenStreetMap contributors"
   * - HTML with links: `© <a href='https://openstreetmap.org'>OpenStreetMap</a> contributors`
   * - Multiple elements: `© <a href='https://osm.org'>OSM</a> | <a href='https://mapbox.com'>Mapbox</a>`
   *
   * Only http/https links are rendered as links — other protocols fall back to plain text.
   */
  const attributions: Attribution[] = []
  for (const attrString of deduplicated) {
    // Parse string for anchor tags using regex
    // Matches: <a href="url" ...>text</a> or <a href='url' ...>text</a>
    const anchorRegex = /<a\s+[^>]*href=['"]([^'"]+)['"][^>]*>(.*?)<\/a>/gi
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = anchorRegex.exec(attrString)) !== null) {
      const [fullMatch, href, linkText] = match
      const matchStart = match.index

      // Add text before this link (if any)
      if (matchStart > lastIndex) {
        const textBefore = attrString.substring(lastIndex, matchStart)
        if (textBefore.trim()) {
          attributions.push({ type: 'text', text: textBefore })
        }
      }

      // Add link type only if http/https URLs
      if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
        attributions.push({ type: 'link', text: linkText, href })
      } else {
        // Invalid URL - render as plain text to preserve attribution
        attributions.push({ type: 'text', text: linkText })
      }

      lastIndex = matchStart + fullMatch.length
    }

    // Add remaining text after last link (or entire string if no links)
    if (lastIndex < attrString.length) {
      const textAfter = attrString.substring(lastIndex)
      if (textAfter.trim()) {
        attributions.push({ type: 'text', text: textAfter })
      }
    }
  }

  return attributions
}

/**
 * Update attribution display from active map sources
 *
 * Gets structured attribution data, this component handles:
 * - Source enumeration (filtering by used/usedForTerrain flags)
 * - HTML parsing from TileJSON attribution strings
 * - Substring deduplication (e.g., "© OSM" removed if "© OSM Contributors" exists)
 * - Security validation (http/https URLs only)
 *
 * Example flow:
 * 1. getAttributions() returns structured data:
 *    [{type: 'text', text: '© '}, {type: 'link', text: 'OpenStreetMap', href: 'https://osm.org'},
 *     {type: 'text', text: ' Contributors'}, {type: 'text', text: ' | © Mapbox'}]
 * 2. Component renders directly with Vue template (links clickable, text safe)
 *
 */
function updateAttributions() {
  const newAttributions = getAttributions()
  // Early return if sources haven't changed (undefined = cache hit)
  // This skips JSON.stringify comparison entirely
  if (newAttributions === undefined) {
    return
  }

  // Defensive: treat null as empty array (undefined handled above)
  const safeNewAttributions = newAttributions || []

  // Only update if attribution has changed (prevents unnecessary re-renders)
  // JSON comparison is acceptable here as attribution updates are infrequent
  if (
    JSON.stringify(safeNewAttributions) !==
    JSON.stringify(attributionNodes.value)
  ) {
    attributionNodes.value = safeNewAttributions
  }
}

const handleSourceData: EventCallback<'map-x-source-data'> = (event) => {
  if (
    event.detail?.sourceDataType === 'metadata' ||
    event.detail?.sourceDataType === 'visibility'
  ) {
    updateAttributions()
  }
}

const handleStyleData: EventCallback<'map-x-style-data'> = (event) => {
  if (event.detail?.dataType === 'style') {
    updateAttributions()
  }
}

function initializeAttributionControl() {
  if (!mapManager) {
    return
  }

  // Initial attribution load
  updateAttributions()

  // Update attribution when sources change (style load, source metadata, terrain, etc.)
  mapManager.events.on('map-x-stylesheet-loaded', updateAttributions)
  mapManager.events.on('map-x-source-data', handleSourceData)
  mapManager.events.on('map-x-style-data', handleStyleData)

  cleanupHandler = () => {
    mapManager.events.off('map-x-stylesheet-loaded', updateAttributions)
    mapManager.events.off('map-x-source-data', handleSourceData)
    mapManager.events.off('map-x-style-data', handleStyleData)
  }
}

onMounted(() => {
  if (mapManager) {
    // Wait for map-x-ready event (stylesheet fully loaded)
    mapManager.events.once('map-x-ready', () => {
      initializeAttributionControl()
    })
  }
})

onUnmounted(() => {
  if (cleanupHandler) {
    cleanupHandler()
  }
})
</script>

<style scoped>
.control-attribution {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
  position: relative;
  /* Offset upwards to align with stacked map controls and avoid visual overlap */
  --control-attribution-top-offset: -54px;
  margin-top: var(--control-attribution-top-offset);
  margin-left: 5px;
}

.control-attribution-button {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  flex-shrink: 0;
  z-index: 2;
  position: relative;
}

.control-attribution-button:focus,
.control-attribution-button:focus-visible {
  outline: revert;
}

.control-attribution-icon {
  background-color: var(--ys-color-icon-reverse);
  border-radius: 100%;
  border: 4px solid var(--ys-color-icon-reverse);
}

.control-attribution-link-list {
  position: absolute;
  bottom: 0;
  left: 0;
  color: var(--ys-color-icon-primary);
  background-color: var(--ys-color-icon-reverse);
  padding: var(--ys-spacing-50) var(--ys-spacing-100);
  padding-left: var(--ys-spacing-400);
  max-width: calc(100vw - 5px);
  pointer-events: auto;
  z-index: 1;
  border-radius: var(--ys-radius-150);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--ys-spacing-50);
  flex-wrap: wrap-reverse;
}

.attribution-link {
  display: inline-flex;
  color: var(--ys-color-icon-primary);
  text-decoration: none;
  white-space: nowrap;
}

.attribution-link:hover ys-text {
  text-decoration: underline;
}
</style>
